"use client";

import React from "react";
import { ArrowRight, ShieldCheck, Stethoscope } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const categories = [{
  id: "canine",
  eyebrow: "CANINE CARE",
  title: "Dog Guidance & Wellness",
  description: "Find evidence-informed clinical guidance on canine preventive care, daily nutrition requirements, behavioral conditioning, and detailed breed profiles.",
  link: "/DogsPage",
  buttonText: "Explore Dogs",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_guidance_wellness.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_guidance_wellness.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_guidance_wellness.webp-wvc-sizes",
    alt: "Golden Retriever resting calmly in an editorial home setting"
  },
  tag: "Veterinary Reviewed",
  icon: Stethoscope
}, {
  id: "feline",
  eyebrow: "FELINE CARE",
  title: "Cat Health & Behavior",
  description: "Read structured research on feline metabolic health, environmental enrichment, dietary transitions, and comprehensive breed-specific medical tendencies.",
  link: "/CatsPage",
  buttonText: "Explore Cats",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_health_behavior.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_health_behavior.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_health_behavior.png-wvc-sizes",
    alt: "British Shorthair cat relaxed on a sunlit neutral surface"
  },
  tag: "Clinical Standard",
  icon: ShieldCheck
}];
export default function SpeciesEntry() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="dogs-cats-entry" data-nav="light" className="bg-background py-12 md:py-20 border-b border-border/80">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-2xl mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            <span className="text-xs font-semibold tracking-wider uppercase text-foreground/70">
              Dedicated Portals
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground text-balance">
            Explore dedicated care by species
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Targeted health frameworks, nutritional benchmarks, and behavioral protocols tailored specifically to dogs and cats.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {categories.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={index} variants={cardVariants} className="group relative flex flex-col justify-between rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-250 hover:border-border/60">
                <div>
                  <div className="relative aspect-16/10 w-full overflow-hidden rounded-xl bg-muted">
                    <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]" />
                    <div className="absolute top-3.5 left-3.5 flex items-center gap-1.5 rounded-md bg-background/95 px-3 py-1 text-xs font-medium text-foreground shadow-xs backdrop-blur-xs border border-border/60">
                      <Icon className="size-3.5 text-accent" />
                      <span>{item.tag}</span>
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="text-xs font-bold tracking-widest text-accent uppercase">
                      {item.eyebrow}
                    </div>
                    <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold text-card-foreground tracking-tight">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-border/70 flex items-center justify-between">
                  <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-5 py-2.5 font-medium transition-colors">
                    <Link to={item.link}>
                      <span>{item.buttonText}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>

                  <span className="text-xs font-medium text-muted-foreground hidden sm:inline-block">
                    Peer-reviewed guidance
                  </span>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}